import React, { ChangeEvent, KeyboardEvent as ReactKeyboardEvent, useState } from "react";
import styled from "@emotion/styled";
import { MergeWithOverwrite } from "src/common";
import { colors } from "src/theme";
import { Flex, InputLabel, Text } from "src/components";

const Input = styled.input((props: { hasError: boolean }) => ({
  boxSizing: 'border-box' as const,
  outline: 'none',
  display: 'block',
  width: '100%',
  height: '44px',
  padding: '9px 12px',
  fontSize: '18px',
  fontWeight: 400,
  fontStyle: 'normal',
  fontFamily: "Red Hat Display",
  lineHeight: '24px',
  color: colors.text1,
  backgroundColor: 'transparent',
  borderRadius: '8px',
  border: `1px solid ${props.hasError ? colors.error1 : colors.border5}`,
  transition: 'border-color .15s ease-in-out, box-shadow .15s ease-in-out',

  '&[disabled]': {
    border: `1px solid ${colors.border6}`,
    color: colors.text4,
    pointerEvents: 'none' as const,
  },

  '&::-moz-placeholder': {
    color: colors.text4,
    opacity: 1,
  },

  '&:-ms-input-placeholder': {
    color: colors.text4,
  },

  '&::-webkit-input-placeholder': {
    color: colors.text4,
  },

  '&:focus::-moz-placeholder': {
    color: 'transparent',
    opacity: 1,
  },

  '&:focus:-ms-input-placeholder': {
    color: 'transparent',
  },

  '&:focus::-webkit-input-placeholder': {
    color: 'transparent',
  },

  '&:focus': {
    borderColor: colors.border4,
  },

  '&[type=number]::-webkit-inner-spin-button, &[type=number]::-webkit-outer-spin-button': {
    WebkitAppearance: 'none' as const,
    MozAppearance: 'none' as const,
    appearance: 'none' as const,
  },
}));

const ValidationError = styled.div<{errorMessageAbsolute: boolean}>(({ errorMessageAbsolute }) => ({
  color: colors.error1,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '1.14',
  marginBottom: 0,
  paddingTop: '4px',
  transition: 'all 0.4s ease',
  position: errorMessageAbsolute ? 'absolute' : 'relative'
}));

const Link = styled.a({
  color: colors.primary,
  textDecoration: 'none',
  cursor: 'pointer',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '20px',
  marginBottom: '4px',
  display: 'inline-block',
  verticalAlign: 'top',
});

const InfoCard = styled.div((props: { show: boolean }) => ({
  width: '100%',
  backgroundColor: colors.primary5,
  borderRadius: '12px',
  overflow: 'hidden',
  maxHeight: props.show ? '500px' : '0',
  opacity: props.show ? 1 : 0,
  transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out, padding 0.3s ease-in-out',
  padding: props.show ? '12px' : '0 12px',
  marginBottom: props.show ? '12px' : '0',
}));

const List = styled.ul({
  listStyle: 'none',
  paddingLeft: '0',
  margin: 0,
});

const ListItem = styled.li({
  position: 'relative',
  paddingLeft: '16px',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '6px',
    top: 'calc(50% - 1.5px)',
    width: '3px',
    height: '3px',
    borderRadius: '50%',
    backgroundColor: colors.primary1,
  }
});

export type InputMBOProps = MergeWithOverwrite<React.InputHTMLAttributes<HTMLInputElement>, {
  label?: string;
  value?: string;
  error?: string;
  onChange?: (value: string) => void;
  errorMessageAbsolute?: boolean;
}
>;

export const InputMBO: React.FC<InputMBOProps> = ({
  label,
  error = '',
  value = '',
  onChange,
  errorMessageAbsolute = true,
  ...props
}) => {

  const [inputValue, setInputValue] = useState(value);
  const [showInfoCard, setShowInfoCard] = useState(false);

  // useEffect(() => {
  //   setInputValue(value);
  // }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {

    let input = e.target.value.replace(/[^\d]/g, '');

    if (input.length > 9) {
      input = input.slice(0, 9);
    }

    setInputValue(input);
    if (onChange) onChange(input);
  }

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
    }
    // Spriječiti unos slova 'e', 'E', '+', '-', '.' i ','
    if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.addEventListener("wheel", (e: WheelEvent) => {
      e.preventDefault();
    }, { passive: false });
  };

  const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setShowInfoCard(!showInfoCard);
  };

  return (
    <div>
      <InfoCard show={showInfoCard}>
        <Text type='tiny' color={colors.primary1}>{'Matični broj osiguranika (MBO) nalazi se na:'}</Text>
        <List>
          <ListItem><Text type='tiny' color={colors.primary1}>{'Novijim osobnim iskaznicama'}</Text></ListItem>
          <ListItem><Text type='tiny' color={colors.primary1}>{'Zdravstvenoj iskaznici'}</Text></ListItem>
          <ListItem><Text type='tiny' color={colors.primary1}>{'Portalu e-Građani'}</Text></ListItem>
        </List>
      </InfoCard>
      {label ? 
        <Flex align='center' justify='space-between' gap={8}>
          <InputLabel htmlFor={props.id} disabled={props.disabled}>{label}</InputLabel>
          <Link href='#' onClick={handleLinkClick} color={colors.primary}>{'Gdje mogu pronaći MBO?'}</Link>
        </Flex> : null}
      <Input
        autoComplete="off"
        {...props}
        type='number'
        id={props.id}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        hasError={!!error}
        value={inputValue}
        maxLength={9}
      />
      {error ? <ValidationError errorMessageAbsolute={errorMessageAbsolute}>{error}</ValidationError> : null}
    </div>
  );
};
